/**
 * Central API client.
 *
 * Customer isolation: every customer API call sends X-Restaurant-Slug header
 * so the server knows which restaurant's customer DB to query.
 * localStorage keys for customer tokens are also scoped per-slug so logging in
 * at yasmin.localhost never bleeds into luna.localhost.
 */
import { API } from './baseURL';

// ── Determine current restaurant slug from subdomain ─────────────────────────
// Mirrors the logic in useRestaurantSlug.js but synchronous and always fresh.
function getCurrentSlug() {
  const parts = window.location.hostname.toLowerCase().split('.');
  const SKIP  = new Set(['www', 'api', 'app', 'mail', 'ftp', 'localhost']);
  // *.localhost  e.g. yasmin.localhost
  if (parts.length === 2 && parts[1] === 'localhost' && !SKIP.has(parts[0]))
    return parts[0];
  // subdomain.domain.tld
  if (parts.length >= 3 && !SKIP.has(parts[0]) && !/^\d+$/.test(parts[0]))
    return parts[0];
  return null;
}

// ── Per-restaurant localStorage keys ────────────────────────────────────────
// On the platform (no slug), we still need admin/owner keys globally.
export const ADMIN_TOKEN_KEY = 'nooshin_admin_token';
export const OWNER_TOKEN_KEY = 'nooshin_owner_token';

// Customer token/profile are scoped: nooshin_customer_token_yasmin
export function getCustomerTokenKey() {
  const slug = getCurrentSlug();
  return slug ? `nooshin_customer_token_${slug}` : 'nooshin_customer_token';
}
export function getCustomerProfileKey() {
  const slug = getCurrentSlug();
  return slug ? `nooshin_customer_profile_${slug}` : 'nooshin_customer_profile';
}
// Keep backward-compat export so AuthContext import doesn't break
export const CUSTOMER_TOKEN_KEY = 'nooshin_customer_token'; // only used as fallback

// ── Core request wrapper ─────────────────────────────────────────────────────
async function request(endpoint, {
  tokenKey,
  body,
  method        = 'GET',
  customerRoute = false,  // if true, adds X-Restaurant-Slug header
} = {}) {
  const headers = { 'Content-Type': 'application/json' };

  // Auth header
  if (tokenKey) {
    const token = localStorage.getItem(tokenKey);
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }

  // Restaurant-scoping header for all customer endpoints
  if (customerRoute) {
    const slug = getCurrentSlug();
    if (slug) headers['X-Restaurant-Slug'] = slug;
  }

  const res = await fetch(`${API}${endpoint}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const data = await res.json().catch(() => ({}));

  // Auto-logout: if server returns 401, clear that token and fire an event
  // so the relevant AuthContext can kick the user out cleanly.
  if (res.status === 401 && tokenKey) {
    localStorage.removeItem(tokenKey);
    window.dispatchEvent(new CustomEvent('nooshin:401', { detail: { tokenKey } }));
  }

  if (!res.ok) throw new Error(data.message || `Server error ${res.status}`);
  return data;
}

// ── Translation (server-side, multi-provider, cached) ────────────────────────
// Routes machine translation through our own server instead of the browser
// hitting flaky public instances directly. Accepts a single string or an array
// and returns an array of translated strings in the same order.
export const translateApi = {
  // review:true runs an AI quality pass that fixes literal/nonsense machine
  // translations after the free providers (e.g. "Mobile First" → "موبایل اول").
  translate: (q, source, target, review = false) =>
    request('/translate', { method: 'POST', body: { q, source, target, review } }),
};

// ── Restaurant — public ──────────────────────────────────────────────────────
// ── Geo (IP → default country for the phone picker) ──────────────────────────
export const geoApi = {
  country: () => request('/geo/country'),
};

export const restaurantApi = {
  getBySlug:     (slug)            => request(`/restaurants/${slug}`),
  getCategories: (slug)            => request(`/restaurants/${slug}/categories`),
  getProducts:   (slug)            => request(`/restaurants/${slug}/products`),
  searchMenu:    (slug, q)         => request(`/restaurants/${slug}/search?q=${encodeURIComponent(q)}`),
  getProduct:    (slug, productId) => request(`/restaurants/${slug}/products/${productId}`),
  getRecommendations: (slug, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/restaurants/${slug}/recommendations${qs ? '?' + qs : ''}`);
  },
  trackOrder:    (slug, orderId)   => request(`/restaurants/${slug}/orders/${orderId}/track`),
  placeOrder:    (slug, payload)   => request(`/restaurants/${slug}/orders`, {
    method: 'POST',
    body:   payload,
    tokenKey:      getCustomerTokenKey(),
    customerRoute: true,
  }),
};

// ── Customer auth ────────────────────────────────────────────────────────────
// All customer routes need X-Restaurant-Slug  →  customerRoute: true
export const customerApi = {
  register: (payload) => request('/customers/register', {
    method: 'POST', body: payload, customerRoute: true,
  }),
  login: (payload) => request('/customers/login', {
    method: 'POST', body: payload, customerRoute: true,
  }),
  // Phone-number (SMS-OTP) login/signup
  otpSend:   (phone)             => request('/customers/otp/send',   { method: 'POST', body: { phone }, customerRoute: true }),
  otpVerify: (phone, code, name) => request('/customers/otp/verify', { method: 'POST', body: { phone, code, name }, customerRoute: true }),
  // Toggle a product in the signed-in customer's favorites
  toggleFavorite: (productId) => request('/customers/me/favorites', { method: 'POST', body: { productId }, customerRoute: true, tokenKey: getCustomerTokenKey() }),
  // Forgot password → reset via SMS code
  pwResetSend:   (phone)                   => request('/customers/password/reset/send',   { method: 'POST', body: { phone }, customerRoute: true }),
  pwResetVerify: (phone, code, newPassword)=> request('/customers/password/reset/verify', { method: 'POST', body: { phone, code, newPassword }, customerRoute: true }),
  getMe: () => request('/customers/me', {
    tokenKey: getCustomerTokenKey(), customerRoute: true,
  }),
  updateMe: (payload) => request('/customers/me', {
    method: 'PATCH', body: payload, tokenKey: getCustomerTokenKey(), customerRoute: true,
  }),
  getOrders: () => request('/customers/me/orders', {
    tokenKey: getCustomerTokenKey(), customerRoute: true,
  }),
  getOrder: (id) => request(`/customers/me/orders/${id}`, {
    tokenKey: getCustomerTokenKey(), customerRoute: true,
  }),
};

// ── Restaurant admin ─────────────────────────────────────────────────────────
export const adminApi = {
  login:            (payload)          => request('/admin/login', { method: 'POST', body: payload }),
  otpSend:   (slug, phone)       => request('/admin/otp/send',   { method: 'POST', body: { phone, restaurantSlug: slug } }),
  otpVerify: (slug, phone, code) => request('/admin/otp/verify', { method: 'POST', body: { phone, code, restaurantSlug: slug } }),
  getRestaurant:    (slug)             => request(`/admin/${slug}/restaurant`,      { tokenKey: ADMIN_TOKEN_KEY }),
  updateRestaurant: (slug, payload)    => request(`/admin/${slug}/restaurant`,      { method: 'PATCH',  body: payload, tokenKey: ADMIN_TOKEN_KEY }),
  // Blog post management (this restaurant's own posts)
  listPosts:    (slug)              => request(`/admin/${slug}/blog/posts`, { tokenKey: ADMIN_TOKEN_KEY }),
  createPost:   (slug, fields)      => request(`/admin/${slug}/blog/posts`, { method: 'POST', body: fields, tokenKey: ADMIN_TOKEN_KEY }),
  generatePost: (slug)              => request(`/admin/${slug}/blog/generate`, { method: 'POST', tokenKey: ADMIN_TOKEN_KEY }),
  editPost:     (slug, ps, fields)  => request(`/admin/${slug}/blog/posts/${encodeURIComponent(ps)}`, { method: 'PATCH', body: fields, tokenKey: ADMIN_TOKEN_KEY }),
  deletePost:   (slug, ps)          => request(`/admin/${slug}/blog/posts/${encodeURIComponent(ps)}`, { method: 'DELETE', tokenKey: ADMIN_TOKEN_KEY }),
  // One-click server-side translation of the whole restaurant into one/all languages.
  // body: { targetLang?, languages?, overwrite? } → returns updated restaurant + summary
  translateRestaurant: (slug, payload = {}) => request(`/admin/${slug}/translate`,   { method: 'POST',   body: payload, tokenKey: ADMIN_TOKEN_KEY }),
  getStats:         (slug)             => request(`/admin/${slug}/stats`,           { tokenKey: ADMIN_TOKEN_KEY }),
  updateMe:         (slug, payload)    => request(`/admin/${slug}/me`,              { method: 'PATCH',  body: payload, tokenKey: ADMIN_TOKEN_KEY }),
  getCategories:    (slug)             => request(`/admin/${slug}/categories`,      { tokenKey: ADMIN_TOKEN_KEY }),
  addCategory:      (slug, payload)    => request(`/admin/${slug}/categories`,      { method: 'POST',   body: payload, tokenKey: ADMIN_TOKEN_KEY }),
  updateCategory:   (slug, id, payload)=> request(`/admin/${slug}/categories/${id}`,{ method: 'PATCH',  body: payload, tokenKey: ADMIN_TOKEN_KEY }),
  deleteCategory:   (slug, id)         => request(`/admin/${slug}/categories/${id}`,{ method: 'DELETE', tokenKey: ADMIN_TOKEN_KEY }),
  getProducts:      (slug)             => request(`/admin/${slug}/products`,        { tokenKey: ADMIN_TOKEN_KEY }),
  addProduct:       (slug, payload)    => request(`/admin/${slug}/products`,        { method: 'POST',   body: payload, tokenKey: ADMIN_TOKEN_KEY }),
  updateProduct:    (slug, pid, payload)=>request(`/admin/${slug}/products/${pid}`, { method: 'PATCH',  body: payload, tokenKey: ADMIN_TOKEN_KEY }),
  toggleProduct:    (slug, pid)        => request(`/admin/${slug}/products/${pid}/toggle`, { method: 'PATCH', tokenKey: ADMIN_TOKEN_KEY }),
  deleteProduct:    (slug, pid)        => request(`/admin/${slug}/products/${pid}`, { method: 'DELETE', tokenKey: ADMIN_TOKEN_KEY }),
  getOrders: (slug, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/admin/${slug}/orders${qs ? '?' + qs : ''}`, { tokenKey: ADMIN_TOKEN_KEY });
  },
  updateOrderStatus: (slug, orderId, status) =>
    request(`/admin/${slug}/orders/${orderId}/status`, { method: 'PATCH', body: { status }, tokenKey: ADMIN_TOKEN_KEY }),

  // Branch menu endpoints
  getBranchCategories:   (slug, bId)           => request(`/admin/${slug}/branches/${bId}/categories`,                   { tokenKey: ADMIN_TOKEN_KEY }),
  addBranchCategory:     (slug, bId, payload)  => request(`/admin/${slug}/branches/${bId}/categories`,                   { method:'POST',   body:payload, tokenKey: ADMIN_TOKEN_KEY }),
  updateBranchCategory:  (slug, bId, cId, p)   => request(`/admin/${slug}/branches/${bId}/categories/${cId}`,            { method:'PATCH',  body:p,       tokenKey: ADMIN_TOKEN_KEY }),
  deleteBranchCategory:  (slug, bId, cId)      => request(`/admin/${slug}/branches/${bId}/categories/${cId}`,            { method:'DELETE',               tokenKey: ADMIN_TOKEN_KEY }),
  getBranchProducts:     (slug, bId)           => request(`/admin/${slug}/branches/${bId}/products`,                     { tokenKey: ADMIN_TOKEN_KEY }),
  addBranchProduct:      (slug, bId, payload)  => request(`/admin/${slug}/branches/${bId}/products`,                     { method:'POST',   body:payload, tokenKey: ADMIN_TOKEN_KEY }),
  updateBranchProduct:   (slug, bId, pId, p)   => request(`/admin/${slug}/branches/${bId}/products/${pId}`,              { method:'PATCH',  body:p,       tokenKey: ADMIN_TOKEN_KEY }),
  toggleBranchProduct:   (slug, bId, pId)      => request(`/admin/${slug}/branches/${bId}/products/${pId}/toggle`,       { method:'PATCH',               tokenKey: ADMIN_TOKEN_KEY }),
  deleteBranchProduct:   (slug, bId, pId)      => request(`/admin/${slug}/branches/${bId}/products/${pId}`,              { method:'DELETE',               tokenKey: ADMIN_TOKEN_KEY }),
  syncBranches:          (slug, payload)       => request(`/admin/${slug}/branches/sync`,                                { method:'POST',   body:payload, tokenKey: ADMIN_TOKEN_KEY }),

  // Events & Promotions
  getAnalytics:       (slug, days=30)     => request(`/admin/${slug}/analytics?days=${days}`,  { tokenKey: ADMIN_TOKEN_KEY }),
  getBehavior:        (slug, days=30)     => request(`/admin/${slug}/behavior?days=${days}`,   { tokenKey: ADMIN_TOKEN_KEY }),
  getAuditLogs:       (slug, params='')   => request(`/admin/${slug}/audit?${params}`,          { tokenKey: ADMIN_TOKEN_KEY }),
  exportAuditCsvUrl:  (slug, params='')  => `${API}/admin/${slug}/audit/export.csv?${params}&token=${typeof window!=='undefined'?localStorage.getItem('nooshin_admin_token'):''}`,
  getOrderStats:      (slug)              => request(`/admin/${slug}/orders/stats`,          { tokenKey: ADMIN_TOKEN_KEY }),
  updateOrderPayment: (slug, id, data)   => request(`/admin/${slug}/orders/${id}/payment`,  { method:'PATCH', body:data, tokenKey: ADMIN_TOKEN_KEY }),

  getEvents:    (slug)            => request(`/admin/${slug}/events`,         { tokenKey: ADMIN_TOKEN_KEY }),
  saveEvent:    (slug, payload)   => request(`/admin/${slug}/events`,         { method:'POST',   body:payload, tokenKey: ADMIN_TOKEN_KEY }),
  updateEvent:  (slug, id, data)  => request(`/admin/${slug}/events/${id}`,   { method:'PATCH',  body:data,    tokenKey: ADMIN_TOKEN_KEY }),
  deleteEvent:  (slug, id)        => request(`/admin/${slug}/events/${id}`,   { method:'DELETE',               tokenKey: ADMIN_TOKEN_KEY }),

  // Customers
  getCustomers:   (slug)             => request(`/admin/${slug}/customers`,              { tokenKey: ADMIN_TOKEN_KEY }),
  updateCustomer: (slug, id, payload)=> request(`/admin/${slug}/customers/${id}`,        { method: 'PATCH', body: payload, tokenKey: ADMIN_TOKEN_KEY }),

  // Sub-admins
  getVisitors:    (slug, days=30)    => request(`/admin/${slug}/visitors?days=${days}`,  { tokenKey: ADMIN_TOKEN_KEY }),
  getSubAdmins:   (slug)             => request(`/admin/${slug}/sub-admins`,             { tokenKey: ADMIN_TOKEN_KEY }),
  createSubAdmin: (slug, payload)    => request(`/admin/${slug}/sub-admins`,             { method: 'POST',  body: payload, tokenKey: ADMIN_TOKEN_KEY }),
  updateSubAdmin: (slug, id, payload)=> request(`/admin/${slug}/sub-admins/${id}`,       { method: 'PATCH', body: payload, tokenKey: ADMIN_TOKEN_KEY }),
  deleteSubAdmin: (slug, id)         => request(`/admin/${slug}/sub-admins/${id}`,       { method: 'DELETE',              tokenKey: ADMIN_TOKEN_KEY }),
};

// ── Business owner ───────────────────────────────────────────────────────────
export const businessApi = {
  register:       (payload) => request('/business/register',    { method: 'POST',  body: payload }),
  login:          (payload) => request('/business/login',       { method: 'POST',  body: payload }),
  otpSend:        (phone)        => request('/business/otp/send',   { method: 'POST', body: { phone } }),
  otpVerify:      (phone, code, name) => request('/business/otp/verify', { method: 'POST', body: { phone, code, name } }),
  getMe:          ()        => request('/business/me',          { tokenKey: OWNER_TOKEN_KEY }),
  getGrowth:      ()        => request('/business/growth',      { tokenKey: OWNER_TOKEN_KEY }),
  updateMe:       (payload) => request('/business/me',          { method: 'PATCH', body: payload, tokenKey: OWNER_TOKEN_KEY }),
  getRestaurants: ()        => request('/business/restaurants', { tokenKey: OWNER_TOKEN_KEY }),
  addRestaurant:  (payload) => request('/business/restaurants', { method: 'POST',  body: payload, tokenKey: OWNER_TOKEN_KEY }),
  updatePlan:     (plan)    => request('/business/plan',        { method: 'PATCH', body: { plan }, tokenKey: OWNER_TOKEN_KEY }),
  updateRestaurant:        (rId, payload) => request(`/business/restaurants/${rId}`,                      { method: 'PATCH',  body: payload, tokenKey: OWNER_TOKEN_KEY }),
  deleteRestaurant:        (rId)          => request(`/business/restaurants/${rId}`,                      { method: 'DELETE',              tokenKey: OWNER_TOKEN_KEY }),
  updateAdminCredentials:  (rId, payload) => request(`/business/restaurants/${rId}/admin-credentials`,   { method: 'PATCH',  body: payload, tokenKey: OWNER_TOKEN_KEY }),
  // Branch management
  getBranches:    (restaurantId)                   => request(`/business/restaurants/${restaurantId}/branches`, { tokenKey: OWNER_TOKEN_KEY }),
  addBranch:      (restaurantId, payload)          => request(`/business/restaurants/${restaurantId}/branches`, { method: 'POST',   body: payload, tokenKey: OWNER_TOKEN_KEY }),
  updateBranch:   (restaurantId, branchId, payload)=> request(`/business/restaurants/${restaurantId}/branches/${branchId}`, { method: 'PATCH', body: payload, tokenKey: OWNER_TOKEN_KEY }),
  getAnalytics:   (slug, days=30)           => request(`/admin/${slug}/analytics?days=${days}`,  { tokenKey: ADMIN_TOKEN_KEY }),
  getAuditLogs:   (slug, params='')         => request(`/admin/${slug}/audit?${params}`,          { tokenKey: ADMIN_TOKEN_KEY }),
  aiAnalyze:      (slug, messages)          => request('/ai-analyze', { method:'POST', body:{ messages, restaurantSlug:slug, scope:'restaurant' } }),
  platformAiAnalyze: (messages)             => request('/ai-analyze', { method:'POST', body:{ messages, scope:'platform' } }),
  deleteBranch:   (restaurantId, branchId)         => request(`/business/restaurants/${restaurantId}/branches/${branchId}`, { method: 'DELETE', tokenKey: OWNER_TOKEN_KEY }),
};

// ── Platform admin ────────────────────────────────────────────────────────────
export const PLATFORM_TOKEN_KEY = 'nooshin_platform_token';

export const platformApi = {
  generatePost: () => request('/platform/blog/generate', { method: 'POST', tokenKey: PLATFORM_TOKEN_KEY }),
  createPost:   (fields) => request('/platform/blog/posts', { method: 'POST', body: fields, tokenKey: PLATFORM_TOKEN_KEY }),
  listPosts:    () => request('/platform/blog/posts', { tokenKey: PLATFORM_TOKEN_KEY }),
  editPost:     (slug, fields) => request('/platform/blog/posts/' + encodeURIComponent(slug), { method: 'PATCH', body: fields, tokenKey: PLATFORM_TOKEN_KEY }),
  deletePost:   (slug) => request('/platform/blog/posts/' + encodeURIComponent(slug), { method: 'DELETE', tokenKey: PLATFORM_TOKEN_KEY }),
  login:       (payload) => request('/platform/login',   { method: 'POST', body: payload }),
  otpSend:     (phone)       => request('/platform/otp/send',   { method: 'POST', body: { phone } }),
  otpVerify:   (phone, code) => request('/platform/otp/verify', { method: 'POST', body: { phone, code } }),
  getMe:       ()        => request('/platform/me',      { tokenKey: PLATFORM_TOKEN_KEY }),
  getStats:    ()        => request('/platform/stats',   { tokenKey: PLATFORM_TOKEN_KEY }),
  // Restaurants
  getRestaurants:    ()           => request('/platform/restaurants',     { tokenKey: PLATFORM_TOKEN_KEY }),
  updateRestaurant:  (id, data)   => request('/platform/restaurants/'+id, { method: 'PATCH', body: data, tokenKey: PLATFORM_TOKEN_KEY }),
  deleteRestaurant:  (id)         => request('/platform/restaurants/'+id, { method: 'DELETE', tokenKey: PLATFORM_TOKEN_KEY }),
  // Owners
  getOwners:    ()        => request('/platform/owners',     { tokenKey: PLATFORM_TOKEN_KEY }),
  updateOwner:  (id, data)=> request('/platform/owners/'+id, { method: 'PATCH', body: data, tokenKey: PLATFORM_TOKEN_KEY }),
  // Platform admins
  getAdmins:    ()        => request('/platform/admins',      { tokenKey: PLATFORM_TOKEN_KEY }),
  createAdmin:  (data)    => request('/platform/admins',      { method: 'POST',   body: data, tokenKey: PLATFORM_TOKEN_KEY }),
  updateAdmin:  (id, data)=> request('/platform/admins/'+id,  { method: 'PATCH',  body: data, tokenKey: PLATFORM_TOKEN_KEY }),
  deleteAdmin:  (id)      => request('/platform/admins/'+id,  { method: 'DELETE', tokenKey: PLATFORM_TOKEN_KEY }),
  // Analytics + Audit
  getPlatformAnalytics: (days=30)       => request(`/platform/analytics?days=${days}`,          { tokenKey: PLATFORM_TOKEN_KEY }),
  getVisitors:          (days=30)       => request(`/platform/visitors?days=${days}`,           { tokenKey: PLATFORM_TOKEN_KEY }),
  getPlatformAudit:     (params='')     => request(`/platform/audit?${params}`,                  { tokenKey: PLATFORM_TOKEN_KEY }),
  exportPlatformAudit:  (params='')     => `${API}/platform/audit/export.csv?${params}`,
  // Settings
  getSettings:    ()       => request('/platform/settings',  { tokenKey: PLATFORM_TOKEN_KEY }),
  updateSettings: (data)   => request('/platform/settings',  { method: 'PATCH', body: data, tokenKey: PLATFORM_TOKEN_KEY }),
  testAi:         ()       => request('/platform/ai/test',   { method: 'POST', tokenKey: PLATFORM_TOKEN_KEY }),
  getSeoFiles:    ()       => request('/platform/seo/files', { tokenKey: PLATFORM_TOKEN_KEY }),

  // Image localization — mirror Iran-blocked remote images onto our own server.
  localizeImages:       () => request('/platform/localize-images',        { method: 'POST', tokenKey: PLATFORM_TOKEN_KEY }),
  localizeImagesStatus: () => request('/platform/localize-images/status', { tokenKey: PLATFORM_TOKEN_KEY }),

  // Unified UI translations — one store for every surface (SSR + SPA).
  getUiTranslations: ()              => request('/platform/i18n', { tokenKey: PLATFORM_TOKEN_KEY }),
  setUiTranslations: (lang, strings) => request('/platform/i18n/' + encodeURIComponent(lang), { method: 'PUT', body: { strings }, tokenKey: PLATFORM_TOKEN_KEY }),
  reviewUiLang:      (lang)          => request('/platform/i18n/' + encodeURIComponent(lang) + '/review', { method: 'POST', tokenKey: PLATFORM_TOKEN_KEY }),
};
